import { Box, useMediaQuery, useTheme } from "@mui/material";
import { GridAddIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import CustomDialog, { FieldType } from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import { getStepsData } from "../../components/ProfileFields";
import ToastNotification from "../../components/ToastNotification";
import { fetchBalanceByProfileId } from "../../services/BalanceService";
import { useCountriesQuery } from "../../services/CountryService";
import {
  getCurrencyNameOptions,
  useCurrenciesQuery,
} from "../../services/CurrencyService";
import { useBillingTermsQuery } from "../../services/FetchBillingTerms";
import { usePaymentTypeQuery } from "../../services/FetchPaymentTypes";
import {
  InvoiceTemplateType,
  useInvoiceTemplateQuery,
} from "../../services/InvoiceTemplateService";
import {
  addProfileApi,
  deleteProfileApi,
  editProfileApi,
  exportProfiles,
  fetchProfileHistory,
  getProfileById,
  IProfile,
  CreditStatus,
  IProfileWithBalance,
  ProfileWithBalanceParams,
  useProfileQuery,
  useProfilesWithBalancesQuery,
} from "../../services/ProfileService";
import {
  fetchUserByEmail,
  useUsersByEmailQuery,
} from "../../services/UsersService";
import { IField, Option, Params } from "../../types/table/interfaces.type";
import AccountNamesTable from "../../components/AccountnameDialog";
import History from "../../components/History";
import { IAutoCompleteProps } from "../../components/common/AutoComplete";
import { useAccountByProfileIdQuery } from "../../services/AccountService";
import { hasJwtPermission } from "../../services/ServicesHelpers";

export interface Currency {
  id: number;
  name: string;
}

// Row colour per credit status (the status comes from the backend)
const CREDIT_STATUS_BG: Record<CreditStatus, string> = {
  USING_FLEX: "#FFE0B2",
  REACHED_LIMIT: "#FFCDD2",
};

const creditStatusOptions: { label: string; value: CreditStatus }[] = [
  { label: "Using flex amount", value: "USING_FLEX" },
  { label: "Reached limit", value: "REACHED_LIMIT" },
];

// The table's filters, also used by Export. IProfile's (unused) balance field
// is left out: the table's balance comes from the DTO.
type ProfileFilters = Params<Omit<IProfile, "balance">> &
  Pick<ProfileWithBalanceParams, "creditStatus">;

/**
 * A balance column, read from the balance admin-api sends with each profile.
 * "-" when the profile came without a balance (e.g. balance-api unavailable).
 */
const balanceColumn = (
  header: string,
  pick: (profile: IProfileWithBalance) => number | undefined,
): IField<IProfileWithBalance> => ({
  key: "balance",
  header,
  transform: (t) => pick(t) ?? "-",
});

const ProfilePage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [filters, setFilters] = useState<
    Params<
      IProfile & { countryId: number; email: string; creditStatus: string }
    >
  >({});
  const [appliedFilters, setAppliedFilters] = useState<ProfileFilters>({});

  const [userOptions, setUserOptions] = useState<Option[]>([]);
  const [countriesOptions, setCountriesOptions] = useState<Option[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<Currency[]>([]);
  const [invoiceTemplateOptions, setInvoiceTemplateOptions] = useState<
    Option[]
  >([]);
  const [paymentTypesOptions, setPaymentTypesOptions] = useState<Option[]>([]);
  const [bilingTermOptions, setBillingTermOptions] = useState<Option[]>([]);

  const dialogRef = useRef<ProfileDialogRef>(null);

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "Accounts" | "history",
    id?: string,
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const { data } = useProfileQuery({ size: 10000 });

  const names =
    data?.content?.map((profile) => ({
      label: profile.name,
      value: profile.id,
    })) || [];

  const { data: fetchUsersByEmail = [] } = useUsersByEmailQuery();
  const { data: invoiceTemplatePage } = useInvoiceTemplateQuery({});
  const { data: fetchCountries } = useCountriesQuery({});
  const { data: fetchCachedPaymentTypes = [] } = usePaymentTypeQuery();
  const { data: fetchCachedBillingTerms = [] } = useBillingTermsQuery();
  const { data: fetchCurrencies = [] } = useCurrenciesQuery({});
  const currencyNameOptions = fetchCurrencies.map((profile) => ({
    label: profile.name,
    value: profile.id,
  }));

  useEffect(() => {
    if (invoiceTemplatePage) {
      const options = invoiceTemplatePage.content.map(
        (template: InvoiceTemplateType) => ({
          label: template.name,
          value: template.id,
        }),
      );
      setInvoiceTemplateOptions(options);
    }
  }, [invoiceTemplatePage]);

  useEffect(() => {
    if (fetchUsersByEmail) {
      setUserOptions(fetchUsersByEmail);
    }
    if (fetchCountries) {
      setCountriesOptions(fetchCountries);
    }
    if (fetchCurrencies) {
      setCurrencyOptions(fetchCurrencies);
    }
    if (fetchCachedPaymentTypes) {
      setPaymentTypesOptions(fetchCachedPaymentTypes);
    }
    if (fetchCachedBillingTerms) {
      setBillingTermOptions(fetchCachedBillingTerms);
    }
  }, [
    fetchUsersByEmail,
    fetchCountries,
    fetchCurrencies,
    fetchCachedPaymentTypes,
    fetchCachedBillingTerms,
  ]);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      id: filters.name,
      userId: filters.email,
      currencyId: filters.currencyId,
      creditStatus: (filters.creditStatus || undefined) as
        | CreditStatus
        | undefined,
      _forceRefresh: Date.now(),
    }));
  };

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Name",
      value: filters.name ?? "",
      options: names,
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = names.find((option) => option.value === value);

        setFilters((prev) => ({
          ...prev,
          name: selectedOperator?.value,
        }));
      },
      clearable: true,
    },
    {
      label: "Employee",
      value: filters.email ?? "",
      options: fetchUsersByEmail.map((option) => ({
        ...option,
        label: option.name,
        value: option.value,
      })),
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          email: String(value),
        }));
      },
      clearable: true,
    },
    {
      label: "Currency",
      value: filters.currencyId ?? "",
      options: currencyNameOptions,

      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = currencyNameOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          currencyId:
            selectedOperator?.value !== undefined
              ? String(selectedOperator.value)
              : "",
        }));
      },
      clearable: true,
    },
    {
      label: "Credit Status",
      value: filters.creditStatus ?? "",
      options: creditStatusOptions,
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          creditStatus: value ? String(value) : undefined,
        }));
      },
      clearable: true,
    },
  ];
  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        console.log(`Edit action triggered for ID: ${id}`);
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_PROFILE"),
    },
    onDelete: {
      handler: async (id: string | number) => {
        console.log(`Delete action triggered for ID: ${id}`);
        handleOpenDialog("delete", String(id));
      },
      isDisabled: !hasJwtPermission("DELETE_PROFILE"),
    },
    // onIPAction: {
    //   isDisabled: true,
    //   handler: (id: string | number) => {
    //     console.log(`IP Action triggered for Identifier: ${id}`);
    //   },
    // },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_PROFILES_HISTORY"),
    },
    onListAccounts: {
      handler: (row: any) => {
        console.log(`List Accounts action triggered for profileId: ${row.id}`);
        handleOpenDialog("Accounts", String(row.id));
      },
      isDisabled: !hasJwtPermission("GET_PROFILE_ACCOUNTS"),
    },
  });

  const fields = useMemo<IField<IProfileWithBalance>[]>(
    () => [
      { key: "name", header: `Name` },
      {
        key: "userId",
        header: "Employee",
        options: (t) => ({
          queryKey: ["user", t.userId],
          queryFn: async () => {
            const user = await fetchUserByEmail(t.userId);
            return user;
          },
          select: (data: any) => data?.email,
        }),
      },
      balanceColumn("Client Usage", (t) => t.clientUsage),
      { key: "clientCreditLimit", header: "Client Balance Limit" },
      balanceColumn("Provider Usage", (t) => t.vendorUsage),
      { key: "providerCreditLimit", header: "Provider Balance Limit" },
      balanceColumn("Balance", (t) => t.balance),
      {
        key: "currencyId",
        header: "Currency",
        options: (t) => getCurrencyNameOptions(Number(t.currencyId)),
      },
      { key: "paymentTerm", header: "Payment Term" },
    ],
    [],
  );

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Account Profile"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm
          autoCompletes={autoCompletes}
          buttons={[
            {
              label: "Search",
              variant: "contained",
              bgColor: COLORS.GOLD,
              onClick: handleSearchClick,
              permission: "GET_ALL_PROFILES",
            },
          ]}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Account Profile List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_PROFILE",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportProfiles(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_PROFILES_CSV",
            },
          ]}
        />

        <CustomTable<IProfileWithBalance>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useProfilesWithBalancesQuery}
          actions={actions}
          shadeRow={(row) => !!row.creditStatus}
          shadeBg={(row) =>
            row.creditStatus && CREDIT_STATUS_BG[row.creditStatus]
          }
          shadeColor={COLORS.typographyColor}
          sortableFields={[
            "paymentTerm",
            "name",
            "clientCreditLimit",
            "providerCreditLimit",
          ]}
        />
      </Box>
      <ProfileDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        countriesOptions={countriesOptions}
        currencyOptions={currencyOptions}
        paymentTypesOptions={paymentTypesOptions}
        bilingTermOptions={bilingTermOptions}
        invoiceTemplateOptions={invoiceTemplateOptions}
        userOptions={userOptions}
        appliedFilters={appliedFilters}
      />
    </Box>
  );
};

export interface ProfileDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "Accounts" | "history",
    id?: string,
  ) => void;
}

interface ProfileDialogProps {
  onRefresh: () => void;
  countriesOptions: Option[];
  paymentTypesOptions: Option[];
  currencyOptions: Currency[];
  appliedFilters: Params<IProfile>;
  invoiceTemplateOptions: Option[];
  bilingTermOptions: Option[];
  userOptions: Option[];
}

const ProfileDialog = forwardRef<ProfileDialogRef, ProfileDialogProps>(
  (
    {
      onRefresh,
      paymentTypesOptions,
      countriesOptions,
      bilingTermOptions,
      currencyOptions,
      appliedFilters,
      invoiceTemplateOptions,
      userOptions,
    },
    ref,
  ) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "Accounts" | "history"
    >("add");
    const [dialogData, setDialogData] = useState<Partial<IProfile>>({});
    const [selectedId, setSelectedId] = useState<string | undefined>();

    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [currentStep, setCurrentStep] = useState(0);
    const [isNextDisabled, setIsNextDisabled] = useState<boolean>(true);
    const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
    const { data: accountNames } = useAccountByProfileIdQuery(
      dialogData.id ? dialogData.id : "",
    );

    useEffect(() => {
      const requiredFields = ["name"];
      setIsSaveDisabled(!requiredFields);
    }, [dialogData]);

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id) => {
        setDialogMode(mode);
        setSelectedId(id);
        setDialogOpen(true);
        openDialog(mode, id);
      },
    }));
    const openDialog = useCallback(
      async (
        mode: "add" | "edit" | "delete" | "Accounts" | "history",
        id?: string,
      ) => {
        setDialogMode(mode);
        try {
          if (mode === "edit" && id) {
            const entityData = await getProfileById(id);

            Object.keys(entityData).forEach((key) => {
              if (
                key.toLowerCase().includes("alert") &&
                typeof entityData[key] === "number"
              ) {
                entityData[key] = (entityData[key] as number) * 100;
              }
            });
            setDialogData(entityData);
          } else if (mode === "delete" && id) {
            setDialogData({ id });
          } else if (mode === "Accounts" && id) {
            setDialogData({ id });
          } else {
            setDialogData({ flexAmount: 0 });
          }
        } catch (error: any) {
          console.error(`Error fetching Profile data for mode ${mode}:`, error);
        }
      },
      [],
    );

    const handleClose = () => {
      setDialogOpen(false);
      if (dialogMode !== "edit") {
        setDialogData({});
      }
      setCurrentStep(0);
    };

    useEffect(() => {
      if (dialogMode === "edit" && selectedId) {
        openDialog("edit", selectedId);
      } else if (dialogMode === "add") {
        openDialog("add");
      }
    }, [dialogMode, selectedId, openDialog]);

    useEffect(() => {
      const stepRequiredFields: Record<number, string[]> = {
        0: [
          "name",
          "accountManagerEmail",
          "address",
          "userId",
          "phoneNumber",
          "ratingEmail",
          "countryId",
          "billingEmail",
          "currencyId",
          "dailyReportEmail",
          "supportEmail",
          "sidRegistrationEmail",
        ],
        1: ["clientInitialBalance", "clientCreditLimit", "flexAmount"],
        2: ["providerInitialBalance", "providerCreditLimit"],
        3: ["paymentTerm", "paymentType", "billingTerm", "invoiceTemplateId"],
      };

      const currentStepRequiredFields = stepRequiredFields[currentStep] || [];
      const areRequiredFieldsValid = currentStepRequiredFields.every(
        (field) => {
          const value = dialogData[field as keyof IProfile];
          return (
            (typeof value === "string" || typeof value === "number") &&
            (value || value === 0) &&
            !errors[field]
          );
        },
      );

      setIsNextDisabled(!areRequiredFieldsValid);
    }, [dialogData, errors, currentStep]);

    const handleNext = () => setCurrentStep((prev) => prev + 1);

    const handleBack = () =>
      setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev));

    const handleSave = async () => {
      try {
        const dataToSave = {
          ...dialogData,
          isDeleted: false,
          usesVpn: dialogData.usesVpn ?? false,
        };
        (Object.keys(dataToSave) as Array<keyof typeof dataToSave>).forEach(
          (key) => {
            if (
              key.toLowerCase().includes("alert") &&
              typeof dataToSave[key] === "number"
            ) {
              (dataToSave as any)[key] = (dataToSave[key] as number) / 100;
            }
          },
        );

        if (dialogMode === "edit") {
          await editProfileApi(dataToSave.id as string, dataToSave);
        } else if (dialogMode === "add") {
          await addProfileApi(dataToSave);
        }

        setDialogOpen(false);
        handleClose();
        onRefresh();
        ToastNotification.success(
          `Profile ${dialogMode === "edit" ? "updated" : "added"} successfully!`,
        );
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const deleteProfile = async () => {
      try {
        if (selectedId) {
          await deleteProfileApi(String(selectedId));
          ToastNotification.success("Profile deleted successfully");
          onRefresh();
          setDialogOpen(false);
        }
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const dialogButtons =
      dialogMode === "history"
        ? []
        : dialogMode === "delete"
          ? [
              {
                label: "Delete",
                variant: "contained" as const,
                bgColor: COLORS.GOLD,
                onClick: deleteProfile,
                permission: "DELETE_PROFILE",
              },
            ]
          : dialogMode === "Accounts"
            ? []
            : [
                ...(currentStep > 0
                  ? [
                      {
                        label: "Back",
                        onClick: handleBack,
                        variant: "contained" as const,
                        bgColor: COLORS.GOLD,
                      },
                    ]
                  : []),
                ...(currentStep < 4
                  ? [
                      {
                        label: "Next",
                        onClick: handleNext,
                        variant: "contained" as const,
                        bgColor: COLORS.GOLD,
                        disabled: isNextDisabled,
                      },
                    ]
                  : [
                      {
                        label: "Save",
                        onClick: handleSave,
                        variant: "contained" as const,
                        bgColor: COLORS.GOLD,
                        disabled: isSaveDisabled,
                        permission:
                          dialogMode === "edit"
                            ? "PUT_PROFILE"
                            : "POST_PROFILE",
                      },
                    ]),
              ];

    const fieldHistory = useMemo<
      IField<IProfile & { historyTimestamp: Date }>[]
    >(
      () => [
        {
          key: "name",
          header: `Name`,
        },
        {
          key: "id",
          header: "Employee",
          options: (t) => ({
            queryKey: ["user", t.userId],
            queryFn: async () => {
              const user = await fetchUserByEmail(t.userId);
              return user;
            },
            select: (data) => data?.email,
          }),
        },

        {
          key: "paymentTerm",
          header: "Payment Term",
        },
        {
          key: "accountManagerEmail",
          header: "Account Manager Email",
        },
        {
          key: "address",
          header: "Address",
        },
        {
          key: "ratingEmail",
          header: "Rate Email",
        },
        {
          key: "billingEmail",
          header: "Billing Email",
        },
        {
          key: "dailyReportEmail",
          header: "Daily Report Email",
        },
        {
          key: "supportEmail",
          header: "Support Email",
        },
        {
          key: "sidRegistrationEmail",
          header: "SID Registration Email",
        },
        {
          key: "rateUpdatesEmail",
          header: "Rate Updates Email",
        },
        {
          key: "clientInitialBalance",
          header: "Client Initial Balance",
        },
        {
          key: "clientCreditLimit",
          header: "Client Balance Limit",
        },
        {
          key: "clientAlert1",
          header: "Client Alert 1",
        },
        {
          key: "clientAlert2",
          header: "Client Alert 2",
        },
        {
          key: "clientAlert3",
          header: "Client Alert 3",
        },
        {
          key: "flexAmount",
          header: "Flex Amount",
        },
        {
          key: "providerInitialBalance",
          header: "Provider Balance",
        },
        {
          key: "providerCreditLimit",
          header: "Provider Balance Limit",
        },
        {
          key: "providerAlert1",
          header: "Provider Alert 1",
        },
        {
          key: "providerAlert1",
          header: "Provider Alert 1",
        },
        {
          key: "providerAlert2",
          header: "Provider Alert 2",
        },
        {
          key: "providerAlert3",
          header: "Provider Alert 3",
        },
        {
          key: "balance",
          header: "Balance",
          options: (t) => ({
            queryKey: ["balance", t.id],
            queryFn: async () => {
              return (await fetchBalanceByProfileId(t.id))?.balance;
            },
            select: (data) => data !== null && data,
          }),
        },
        {
          key: "currencyId",
          header: "Currency",
          options: (t) => getCurrencyNameOptions(Number(t.currencyId)),
        },
        {
          key: "invoiceReference",
          header: "Invoice Reference",
        },
        {
          key: "paymentType",
          header: "Invoice Reference",
        },
        {
          key: "billingTerm",
          header: "Billing Term",
        },
        {
          key: "beneficiaryName",
          header: "Beneficiary Name",
        },
        {
          key: "iban",
          header: "IBAN",
        },
        {
          key: "bankName",
          header: "Bank Name",
        },
        {
          key: "swiftCode",
          header: "SWIFT Code",
        },
        {
          key: "bankAddress",
          header: "Bank Address",
        },
        {
          key: "bankPhoneNumber",
          header: "Bank Phone Number",
        },
        {
          key: "accountNumber",
          header: "Bank Phone Number",
        },
        {
          key: "historyTimestamp",
          header: "Date Modified",
        },
        { key: "createdBy", header: "Created By" },
        { key: "updatedBy", header: "Updated By" },
      ],
      [],
    );

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={handleClose}
        mode={dialogMode}
        stepperSx={{
          width: dialogMode === "delete" ? "300px" : "600px",
          textAlign: dialogMode === "delete" ? "left" : "center",
        }}
        title={dialogMode === "history" ? "History" : ""}
        stepLabels={
          dialogMode === "history"
            ? []
            : dialogMode === "delete"
              ? ["Delete Model"]
              : dialogMode === "Accounts"
                ? ["Accounts List"]
                : [
                    "Details",
                    "Client Credit Details",
                    "Vendor Credit Details",
                    "Invoicing Details",
                    "Bank Details",
                  ]
        }
        activeStep={dialogMode === "delete" ? undefined : currentStep}
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={async ({ size = 10 }) => {
                        const result = await fetchProfileHistory(
                          String(selectedId),
                          {
                            size,
                          },
                        );
                        return {
                          ...result,
                          content: result.content.map((item: IProfile) => ({
                            ...item,
                            historyTimestamp:
                              (item as any).historyTimestamp ?? new Date(),
                          })),
                        };
                      }}
                    />
                  ),
                },
              ]
            : dialogMode === "delete"
              ? [
                  {
                    type: "custom",
                    component: (
                      <Box>Are you sure you want to delete this profile?</Box>
                    ),
                  },
                ]
              : dialogMode === "Accounts"
                ? [
                    {
                      type: "custom",
                      component: (
                        <AccountNamesTable
                          accountNames={
                            Array.isArray(accountNames) ? accountNames : []
                          }
                        />
                      ),
                    },
                  ]
                : (getStepsData(
                    dialogData,
                    errors,
                    setDialogData,
                    setErrors,
                    userOptions,
                    countriesOptions,
                    currencyOptions,
                    paymentTypesOptions,
                    bilingTermOptions,
                    invoiceTemplateOptions,
                    (file) => setDialogData((prev) => ({ ...prev, file })),
                  )[currentStep] as FieldType[])
        }
        fieldsPerRow={dialogMode === "delete" ? undefined : 2}
        buttons={dialogButtons}
      />
    );
  },
);

export default ProfilePage;
